import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MagnifyingGlassIcon,
  XCircleIcon,
  ServerIcon,
  DevicePhoneMobileIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import { adminApi, Session } from '../utils/api';
import { formatDistanceToNow } from 'date-fns';
import ForceDisconnectModal from '../components/ForceDisconnectModal';

// Constants
const SESSION_REFETCH_INTERVAL_MS = 15000; // 15 seconds

const SessionManagement: React.FC = () => {
  const [userIdFilter, setUserIdFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState<Session | null>(null);
  const queryClient = useQueryClient();

  // Fetch sessions with filtering and auto-refresh
  const { data: sessions, isLoading, error, dataUpdatedAt, isFetching } = useQuery<Session[]>({
    queryKey: ['admin-sessions', { userIdFilter, statusFilter }],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (userIdFilter) params.user_id = userIdFilter;
      if (statusFilter) params.status_filter = statusFilter;

      const response = await adminApi.getSessions(params);
      return response.data;
    },
    refetchInterval: SESSION_REFETCH_INTERVAL_MS,
  });

  // Delete session mutation
  const deleteSessionMutation = useMutation({
    mutationFn: (sessionId: string) => adminApi.deleteSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-sessions'] });
      setSelectedSession(null);
      setConfirmDisconnect(null);
    },
  });

  const handleForceDisconnect = (session: Session) => {
    setConfirmDisconnect(session);
  };

  const confirmForceDisconnect = () => {
    if (confirmDisconnect) {
      deleteSessionMutation.mutate(confirmDisconnect.id);
    }
  };

  // Memoize active session count to avoid recalculating on every render
  const activeSessionsCount = useMemo(
    () => sessions?.filter(s => s.status === 'active').length || 0,
    [sessions]
  );

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-gray-100 text-gray-800',
      failed: 'bg-red-100 text-red-800',
      expired: 'bg-orange-100 text-orange-800',
    };
    const style = styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800';

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${style}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatBytes = (bytes?: number) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  if (isLoading) {
    return <div className="text-center py-12">Loading sessions...</div>;
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">
        Error loading sessions: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Session Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Monitor and manage all VPN sessions across the platform
          </p>
        </div>

        {/* Real-time Session Stats & Update Indicator */}
        <div className="mt-4 md:mt-0 md:ml-4 flex flex-col gap-2">
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-700">
              <span className="font-semibold">{sessions?.length || 0}</span> total sessions
            </div>
            <div className="text-sm text-gray-700">
              <span className="font-semibold text-green-600">
                {activeSessionsCount}
              </span> active
            </div>
          </div>
          <div className="text-xs text-gray-500 flex items-center gap-2">
            {isFetching && (
              <span className="inline-flex items-center">
                <svg className="animate-spin h-3 w-3 text-blue-500 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Updating...
              </span>
            )}
            {!isFetching && dataUpdatedAt && (
              <span>
                Updated {new Date(dataUpdatedAt).toLocaleTimeString()}
              </span>
            )}
            <span className="text-gray-400">• Auto-refresh: {SESSION_REFETCH_INTERVAL_MS / 1000}s</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label htmlFor="search-user" className="block text-sm font-medium text-gray-700 mb-2">
              Filter by User ID
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="search-user"
                className="admin-input pl-10"
                placeholder="Enter user ID (host or client)..."
                value={userIdFilter}
                onChange={(e) => setUserIdFilter(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-2">
              Status Filter
            </label>
            <select
              id="status-filter"
              className="admin-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="failed">Failed</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>
      </div>

      {/* Sessions Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Session
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Host
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Host Device
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Used
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sessions?.map((session) => (
                <tr key={session.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-indigo-600 flex items-center justify-center">
                          <ServerIcon className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {session.session_name || 'Unnamed Session'}
                        </div>
                        <div className="text-xs text-gray-500 font-mono">{session.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {session.host_display_name || 'Unknown'}
                    </div>
                    <div className="text-xs text-gray-500 font-mono">{session.host_user_id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {session.client_display_name || 'Unknown'}
                    </div>
                    <div className="text-xs text-gray-500 font-mono">{session.client_user_id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {session.host_device_id ? (
                      <div className="flex items-center space-x-2">
                        <DevicePhoneMobileIcon className="h-4 w-4 text-gray-500" />
                        <div>
                          <div className="text-sm text-gray-900">
                            {session.host_device_name || 'Unknown Device'}
                          </div>
                          <div className="text-xs text-gray-500 font-mono">{session.host_device_id}</div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">User-level</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(session.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatBytes(session.data_used_bytes)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(session.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => setSelectedSession(session)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Details
                    </button>
                    {(session.status === 'pending' || session.status === 'active') && (
                      <button
                        onClick={() => handleForceDisconnect(session)}
                        disabled={deleteSessionMutation.isPending}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50 flex items-center gap-1"
                      >
                        <BoltIcon className="h-4 w-4" />
                        Force Disconnect
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {sessions && sessions.length === 0 && (
          <div className="text-center py-12">
            <ServerIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No sessions found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your filters.
            </p>
          </div>
        )}
      </div>

      {/* Session Details Modal */}
      {selectedSession && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Session Details</h3>
                <button
                  onClick={() => setSelectedSession(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Session Name</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedSession.session_name || 'Unnamed Session'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <p className="mt-1">{getStatusBadge(selectedSession.status)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Host User</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedSession.host_display_name || 'Unknown'}
                    </p>
                    <p className="text-xs text-gray-500 font-mono">{selectedSession.host_user_id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Client User</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedSession.client_display_name || 'Unknown'}
                    </p>
                    <p className="text-xs text-gray-500 font-mono">{selectedSession.client_user_id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Client IP</label>
                    <p className="mt-1 text-sm font-mono text-gray-900">
                      {selectedSession.client_ip || 'Not assigned'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Data Used</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {formatBytes(selectedSession.data_used_bytes)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Created</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {formatDate(selectedSession.created_at)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Started</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {formatDate(selectedSession.started_at)}
                    </p>
                  </div>
                  {selectedSession.ended_at && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Ended</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {formatDate(selectedSession.ended_at)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Device Information */}
                {selectedSession.host_device_id && (
                  <div className="pt-4 mt-4 border-t">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">
                      Host Device
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex items-center space-x-2">
                        <DevicePhoneMobileIcon className="h-5 w-5 text-gray-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {selectedSession.host_device_name || 'Unknown Device'}
                          </p>
                          <p className="text-xs text-gray-500 font-mono">
                            {selectedSession.host_device_id}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Session ID */}
                <div className="pt-4 mt-4 border-t">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Session ID</label>
                  <p className="text-xs text-gray-600 font-mono break-all bg-gray-50 p-2 rounded">
                    {selectedSession.id}
                  </p>
                </div>
              </div>

              <div className="mt-5 flex justify-end">
                <button
                  onClick={() => setSelectedSession(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Force Disconnect Confirmation Modal */}
      {confirmDisconnect && (
        <ForceDisconnectModal
          session={confirmDisconnect}
          isDisconnecting={deleteSessionMutation.isPending}
          onConfirm={confirmForceDisconnect}
          onCancel={() => setConfirmDisconnect(null)}
        />
      )}
    </div>
  );
};

export default SessionManagement;
